import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {object, SchemaOf} from 'yup';

import {useEndWorkshopInterestMutation} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, useCodeList} from '@dms/shared';

import {Nullish, RequiredTestIdProps, suffixTestId, yupString} from 'shared/web';

type EndCaseForm = {
  closeNote?: string | Nullish;
  closeReasonCodeId: string;
};

interface WorkshopInterestEndCaseDialogProps extends RequiredTestIdProps {
  interestId: string;
  onSubmit?: () => void;
}

export function WorkshopInterestEndCaseDialog(props: WorkshopInterestEndCaseDialogProps) {
  const [closeReasonList, {isLoading: isCodeListLoading, isError}] = useCodeList(
    'workshop_interest_close_reason'
  );

  const [endWorkshopInterest, {isLoading: isEndLoading}] = useEndWorkshopInterestMutation();

  const onSubmit: FormSubmitHandler<EndCaseForm> = async (data) => {
    await endWorkshopInterest({
      id: props.interestId,
      endWorkshopInterestRequestBody: {
        closeReasonCodeId: data.closeReasonCodeId,
        closeNote: data.closeNote ?? null,
      },
    })
      .unwrap()
      .then(() => props.onSubmit?.())
      .then(() => closeCurrentDialog())
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isCodeListLoading} isError={isError} minHeight={25}>
      <Form<EndCaseForm> schema={schema} onSubmit={onSubmit}>
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              type="choice"
              name="closeReasonCodeId"
              label={i18n.t('general.labels.reason')}
              isRequired
              options={
                closeReasonList?.map((listItem) => ({
                  label: listItem.name,
                  value: listItem.codeId,
                })) ?? []
              }
              data-testid={suffixTestId('closeReasonCodeId', props)}
            />
            <FormField
              control={control}
              type="textarea"
              name="closeNote"
              label={i18n.t('general.labels.note')}
              minRows={3}
              data-testid={suffixTestId('closeNote', props)}
            />
            <ButtonGroup align="right">
              <Button
                variant="secondary"
                onClick={closeCurrentDialog}
                isDisabled={isEndLoading}
                title={i18n.t('general.actions.discard')}
                data-testid={suffixTestId('cancel', props)}
              />
              <FormButton
                variant="primary"
                type="submit"
                control={control}
                isLoading={isEndLoading}
                title={i18n.t('general.actions.endCase')}
                data-testid={suffixTestId('endCase', props)}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}

const schema: SchemaOf<EndCaseForm> = object({
  closeReasonCodeId: yupString.required(),
  closeNote: yupString,
});
